import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Mail, MessageCircle, Instagram, ChevronDown } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PACKAGES = ["Not sure yet", "Wanderlust", "Venture", "Odyssey"];

const INFO = [
  {
    key: "whatsapp",
    icon: MessageCircle,
    label: "Message us on WhatsApp",
    value: "+974 7738 0447",
    href: "https://wa.me/97477380447",
  },
  {
    key: "email",
    icon: Mail,
    label: "Email us",
    value: "contact@triodocz.com",
    href: "mailto:contact@triodocz.com",
  },
  {
    key: "instagram",
    icon: Instagram,
    label: "Connect with us on Instagram",
    value: "@triodocz",
    href: "https://instagram.com/triodocz",
  },
];

const inputCls =
  "w-full bg-white border border-slate-200 px-5 py-4 text-sm text-slate-900 placeholder:text-slate-400 outline-none focus:border-slate-900 transition-colors duration-300";

export const Contact = ({ selectedPackage, setSelectedPackage }) => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, { ...form, package: selectedPackage });
      toast.success("Enquiry sent — we'll reply within one business day.");
      setForm({ name: "", email: "", message: "" });
      setSelectedPackage("Not sure yet");
    } catch (err) {
      toast.error("Something went wrong. Please email us at contact@triodocz.com");
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="px-6 md:px-12 py-28 md:py-36 bg-[#F3F3F1] border-t border-slate-200"
    >
      <div className="max-w-[1400px] mx-auto grid lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: EASE }}
            className="text-xs font-semibold tracking-[0.25em] uppercase text-slate-500 mb-5"
          >
            Contact us
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
            className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-slate-900 leading-[1.1]"
          >
            Tell us where you're{" "}
            <em className="italic text-slate-500">going.</em>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
            className="mt-7 text-sm text-slate-600 leading-relaxed max-w-sm"
          >
            A few lines about your trip is all it takes. A specialist replies
            with the exact forms, documents and timeline for your case.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.3, ease: EASE }}
            className="mt-12 space-y-6"
          >
            {INFO.map((row) => (
              <a
                key={row.key}
                href={row.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`contact-info-${row.key}`}
                className="group flex items-center gap-4 w-fit"
              >
                <span className="w-10 h-10 flex items-center justify-center border border-slate-300 rounded-full transition-colors duration-300 group-hover:border-slate-900 group-hover:bg-slate-900">
                  <row.icon className="w-4 h-4 text-slate-700 transition-colors duration-300 group-hover:text-white" />
                </span>
                <div>
                  <p className="text-[11px] tracking-[0.2em] uppercase text-slate-500">{row.label}</p>
                  <p className="text-sm font-semibold text-slate-900 mt-0.5 transition-colors duration-300 group-hover:text-slate-600">{row.value}</p>
                </div>
              </a>
            ))}
          </motion.div>
        </div>

        <motion.form
          onSubmit={submit}
          data-testid="contact-form"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
          className="lg:col-span-7 bg-white border border-slate-200 p-8 md:p-12"
        >
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <label htmlFor="contact-name" className="block text-[11px] font-semibold tracking-[0.2em] uppercase text-slate-500 mb-2.5">
                Full name
              </label>
              <input
                id="contact-name"
                data-testid="contact-name-input"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Amelia Laurent"
                className={inputCls}
              />
            </div>
            <div>
              <label htmlFor="contact-email" className="block text-[11px] font-semibold tracking-[0.2em] uppercase text-slate-500 mb-2.5">
                Email
              </label>
              <input
                id="contact-email"
                data-testid="contact-email-input"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="amelia@example.com"
                className={inputCls}
              />
            </div>
          </div>

          <div className="mt-6">
            <label htmlFor="contact-package" className="block text-[11px] font-semibold tracking-[0.2em] uppercase text-slate-500 mb-2.5">
              Package
            </label>
            <div className="relative">
              <select
                id="contact-package"
                data-testid="contact-package-select"
                value={selectedPackage}
                onChange={(e) => setSelectedPackage(e.target.value)}
                className={`${inputCls} appearance-none pr-12 cursor-pointer`}
              >
                {PACKAGES.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
              <ChevronDown className="w-4 h-4 text-slate-400 absolute right-5 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
          </div>

          <div className="mt-6">
            <label htmlFor="contact-message" className="block text-[11px] font-semibold tracking-[0.2em] uppercase text-slate-500 mb-2.5">
              Your trip
            </label>
            <textarea
              id="contact-message"
              data-testid="contact-message-input"
              required
              rows={5}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="Destination, travel dates, and anything unusual about your case…"
              className={`${inputCls} resize-none`}
            />
          </div>

          <button
            type="submit"
            data-testid="contact-submit-button"
            disabled={sending}
            className="group mt-10 flex items-center justify-center gap-3 w-full bg-slate-900 text-white text-sm font-semibold py-4.5 px-8 h-[56px] rounded-full hover:bg-slate-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors duration-300"
          >
            {sending ? "Sending…" : "Send enquiry"}
            {!sending && (
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            )}
          </button>
          <p className="mt-5 text-xs text-slate-400 leading-relaxed">
            Your details go straight to a Triodocz specialist and are never
            shared. We reply from contact@triodocz.com.
          </p>
        </motion.form>
      </div>
    </section>
  );
};
